import React, { Component } from 'react';
import { connect } from 'react-redux';
// import { store } from '../../store';
import Moment from 'moment';
import { isObject, omit, startCase } from 'lodash';

import api from '../../store/api/api';
import { setFocusedMembership } from '../../store/api/calls';

import FloatHeader from '../theme/headers/FloatHeader';

import { getUUID } from '../../utils/hashAndUuid';
import FeedbackTile from '../theme/modals/FeedbackTile';
import BaseModal from '../theme/modals/BaseModal';
import ScriptDisplay from '../theme/scripts/scriptDisplay';
import Input from '../theme/forms/Input';
import RandomizerForm from './RandomizerForm';
import VoterLookupForm from './VoterLookupForm';
import VoterRoutedForm from './VoterRoutedForm';
import ScriptEditSidebarForm from './ScriptEditSidebarForm';
import { withRouter } from '../../utils/withRouter';
import AttribsFields, { asAttribsObject } from '../theme/forms/AttribsFields';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

/*
Attrib fields shared by several element types. See `attribsField` on the
element type definitions below for the full schema contract.
*/

const REQUIRED_FIELD = {
  field_name: 'required',
  label: 'Required',
  datatype: 'boolean',
};

const VOTER_SELECTION_FIELDS = [
  {
    field_name: 'check_prior_voter_selection',
    label: 'Check whether the voter was already selected',
    datatype: 'boolean',
    default: true,
  },
  {
    field_name: 'allow_selection_of_prior_selected_voter',
    label: 'Allow selecting an already-selected voter',
    datatype: 'boolean',
    default: true,
    helperText: 'Only applies when the prior-selection check is on.',
  },
];

const SCANTYPE_FIELD = {
  field_name: 'scantype',
  label: 'Scanner Type',
  datatype: 'string',
  suggestions: ['mlkit-plugin'],
  helperText: 'Use "mlkit-plugin" for the Android ML Kit scanner. Empty uses the built-in camera.',
};

const DOCSIZE_FIELD = {
  field_name: 'docsize',
  label: 'Document Size',
  datatype: 'string',
  suggestions: ['8.5x14', '8.5x11', '8.5x8.5'],
  helperText: 'Width x height, used for the capture aspect ratio. Defaults to 8.5x14.',
};

/** Hidden attribs applied on save for image_redactor (not shown in attribsField). */
function finalizeImageRedactorAttribs(attribs) {
  const next = { ...asAttribsObject(attribs) };
  // v4 descriptors are whole files; partial merges via radactionRaw are no longer supported.
  delete next.radactionRaw;
  return next;
}

//form for creating script elements
class CreateElementForm extends Component {
  static defaultProps = {
    editing_element: {},
    script_elements: [],
    onSubmit: () => null,
    onCancel: () => null,
  };

  constructor(props) {
    super(props);
    const elementTypeName =
      props?.editing_element?.elementtype ||
      props?.editing_element?.element_type_config?.name;
    this.makeTextArea = elementTypeName === 'speech' || elementTypeName === 'audio';

    this.state = {
      editing_element: {},
    };

    this.scriptDisplayRef = React.createRef();
  }

  componentDidMount() {
    //we need to move editing_element to state, because we update that value dynamically.
    let { editing_element } = this.props;

    //element came from a creation button. We define the basics here.
    if (!editing_element.element_hash) {
      editing_element = {
        ...editing_element,
        display_name: startCase(editing_element.element_type_config?.name),
        element_hash: getUUID().split('-')[0],
        ...this.getNewElementDefaultPosition(),
        prompt: '',
        elementtype: editing_element.element_type_config?.name,
        iconClass: editing_element.element_type_config?.iconClass,
        connectors: [],
      };
    }

    if (editing_element.element_type_config?.attribsField) {
      editing_element = {
        ...editing_element,
        attribs: asAttribsObject(editing_element.attribs),
      };
    }

    this.setState({ editing_element }, () => {
      if (
        editing_element.element_type_config.defineResponses &&
        !editing_element.connectors?.length
      ) {
        this.addResponseInput();
      }
    });
  }

  renderRandomizer() {
    const element = this.props.editing_element.elementtype
      ? this.props.editing_element
      : {
        display_name: startCase(this.props.editing_element.element_type_config.name),
        element_hash: getUUID().split('-')[0],
        ...this.getNewElementDefaultPosition(),
        prompt: '',
        elementtype: this.props.editing_element.element_type_config.name,
        iconClass: this.props.editing_element.element_type_config.iconClass,
        connectors: [],
      };
    return (
      <RandomizerForm
        element={element}
        onSubmit={this.props.onSubmit}
        onCancel={this.props.onCancel}
        onDeleteElement={this.props.onDeleteElement}
      />
    );
  }

  renderVoterLookup() {
    const element = this.props.editing_element.elementtype
      ? this.props.editing_element
      : {
        display_name: startCase(this.props.editing_element.element_type_config.name),
        element_hash: getUUID().split('-')[0],
        ...this.getNewElementDefaultPosition(),
        prompt: this.props.editing_element?.prompt || '',
        attribs: this.props.editing_element?.attribs || {
          check_prior_voter_selection: true,
          allow_selection_of_prior_selected_voter: true,
        },
        elementtype: this.props.editing_element.element_type_config.name,
        iconClass: this.props.editing_element.element_type_config.iconClass,
        connectors: [],
      };

    const WrapperElement =
      this.props.editing_element.elementtype === 'voterrouted' ||
        this.props.editing_element?.element_type_config?.name === 'voterrouted'
        ? VoterRoutedForm
        : VoterLookupForm;

    return (
      <WrapperElement
        element={element}
        attribsField={this.props.editing_element.element_type_config?.attribsField}
        onSubmit={this.props.onSubmit}
        onCancel={this.props.onCancel}
        onDeleteElement={this.props.onDeleteElement}
      />
    );
  }

  getNewElementDefaultPosition = () => {
    const validTops = this.props.script_elements
      .map((d) => Number(d.pos_top))
      .filter((v) => Number.isFinite(v));

    const validLefts = this.props.script_elements
      .map((d) => Number(d.pos_left))
      .filter((v) => Number.isFinite(v));

    const baseTop = validTops.length ? Math.max(...validTops) : 100;
    const baseLeft = validLefts.length ? Math.max(...validLefts) : 100;

    return {
      pos_top: baseTop + 80,
      pos_left: baseLeft + 80,
    };
  };

  getResponseOptionDataKey = (index) => `response__${index}`;

  addResponseInput = () => {
    let { editing_element } = this.state;
    const connectors = [...(editing_element.connectors || [])];
    const next_el_hash = connectors[connectors.length - 1]?.next_el_hash;

    connectors.push({
      ordinal_position: connectors.length + 1,
      value_text: null,
      next_el_hash,
      key: null,
    });

    this.setState({
      editing_element: { ...editing_element, connectors },
    });
  };

  removeResponseInput = (resp_idx) => {
    let { editing_element } = this.state;

    if (editing_element.connectors.length === 1) {
      this.setState({
        errMsg: 'You must have at least one response for this element.',
      });
      setTimeout(() => this.setState({ errMsg: null }), 2000);
      return;
    }

    const connectors = editing_element.connectors
      .filter((_, i) => i !== resp_idx)
      .map((d, i) => ({
        ...d,
        ordinal_position: i + 1,
        key: `${d.key || i}-${Math.round(Math.random() * 10000000)}`,
      }));

    this.setState({
      editing_element: { ...editing_element, connectors },
    });
  };

  onValueChange = (dataKey, val) => {
    let { editing_element } = this.state;

    if (dataKey.startsWith('response__')) {
      let resp_idx = +dataKey.split('__')[1];
      if (isNaN(resp_idx)) {
        console.error('Unable to get the index of the updated response. DataKey: ', dataKey);
        return;
      }

      let targetResp = editing_element?.connectors[resp_idx];
      if (!targetResp || !isObject(targetResp)) {
        console.error('Could not find target response on update');
        return;
      }

      targetResp = {
        ...targetResp,
        value_text: val,
        key: `${val}-${editing_element.element_hash}`,
      };
      editing_element.connectors[resp_idx] = targetResp;
    } else {
      editing_element[dataKey] = val;
    }

    this.setState({ editing_element });
  };

  onCreateElementSubmit = () => {
    let editing_element = { ...this.state.editing_element };

    if (editing_element.element_type_config?.attribsField) {
      let attribs = asAttribsObject(editing_element.attribs);
      if (editing_element.element_type_config?.name === 'image_redactor') {
        attribs = finalizeImageRedactorAttribs(attribs);
      }
      editing_element.attribs = attribs;
    }

    //filter our elements without values
    editing_element.connectors = editing_element.connectors.filter(
      (d) => d.value_text && d.value_text.length
    );

    this.props.onSubmit(omit(editing_element, ['element_type_config']));
  };

  render() {
    if (
      this.props.editing_element.elementtype === 'randomizer' ||
      this.props.editing_element?.element_type_config?.name === 'randomizer'
    ) {
      return this.renderRandomizer();
    }
    if (
      this.props.editing_element.elementtype === 'voter_lookup' ||
      this.props.editing_element?.element_type_config?.name === 'voter_lookup'
    ) {
      return this.renderVoterLookup();
    }
    if (
      this.props.editing_element.elementtype === 'voterrouted' ||
      this.props.editing_element?.element_type_config?.name === 'voterrouted'
    ) {
      return this.renderVoterLookup();
    }

    let element_type_config = this.props.editing_element.element_type_config;
    const { editing_element } = this.state;

    return (
      <ScriptEditSidebarForm
        title={`${startCase(element_type_config.name)}`}
        iconClass={element_type_config.iconClass}
        displayName={editing_element.display_name || ''}
        onDisplayNameChange={(val) => this.onValueChange('display_name', val)}
        prompt={editing_element.prompt || ''}
        onPromptChange={(val) => this.onValueChange('prompt', val)}
        multilinePrompt={this.makeTextArea}
        errMsg={this.state.errMsg}
        onSubmit={this.onCreateElementSubmit}
        onCancel={this.props.onCancel}
        onDeleteElement={this.props.onDeleteElement}
        showDelete={!!this.props.editing_element.element_hash}
      >
        <Stack spacing={1.25}>
          {element_type_config.defineResponses &&
            (editing_element.connectors || []).map((conn, index) => (
              <Stack key={conn.key || `response-${index}`} direction="row" spacing={0.5} alignItems="flex-start">
                <TextField
                  fullWidth
                  size="small"
                  label={`Response Option ${index + 1}`}
                  value={conn.value_text || ''}
                  onChange={(e) =>
                    this.onValueChange(this.getResponseOptionDataKey(index), e.target.value)
                  }
                  variant="outlined"
                />
                {editing_element.connectors.length > 1 && (
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={`Remove response option ${index + 1}`}
                    onClick={() => this.removeResponseInput(index)}
                    sx={{ mt: 0.5 }}
                  >
                    <Box component="i" className="fas fa-minus-circle" sx={{ fontSize: '0.9rem' }} />
                  </IconButton>
                )}
              </Stack>
            ))}

          {element_type_config.defineResponses && (
            <Button
              size="small"
              color="secondary"
              onClick={this.addResponseInput}
              sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0 }}
            >
              + Response Option
            </Button>
          )}

          {!!element_type_config.attribsField && (
            <AttribsFields
              fields={element_type_config.attribsField}
              value={editing_element.attribs}
              onChange={(attribs) => this.onValueChange('attribs', attribs)}
            />
          )}
        </Stack>
      </ScriptEditSidebarForm>
    );
  }
}

class ScriptEdit extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      error: false,
      errMsg: "Doesn't work yet.",
      editing_element: null,
      script_metadata: {},
      script_elements: [],
      script_revisions: [],
      displayed_revision: null,
      copyModalOpen: false,
      copyTargetCampaign: props.focusedCampaign?.id,
      copyName: '',
      copying: false,
    };

    this.elementTypes = [
      {
        id: 1,
        name: 'speech',
        iconClass: 'fas fa-quote-left',
      },
      {
        id: 2,
        name: 'question',
        iconClass: 'fas fa-question',
        defineResponses: true,
        attribsField: [REQUIRED_FIELD],
      },
      {
        id: 4,
        name: 'phone',
        iconClass: 'fas fa-phone',
        attribsField: [REQUIRED_FIELD],
      },
      {
        id: 5,
        name: 'email',
        iconClass: 'fas fa-at',
        attribsField: [REQUIRED_FIELD],
      },
      {
        id: 6,
        name: 'text',
        iconClass: 'fas fa-pencil-alt',
        attribsField: [REQUIRED_FIELD],
      },
      {
        id: 7,
        name: 'signature',
        iconClass: 'fas fa-signature',
        attribsField: [REQUIRED_FIELD],
      },
      {
        id: 8,
        name: 'barcode',
        iconClass: 'fas fa-barcode',
        attribsField: [REQUIRED_FIELD],
      },
      {
        id: 17,
        name: 'image',
        iconClass: 'far fa-image',
        attribsField: [
          {
            field_name: 'show_upload',
            label: 'Allow choosing an existing photo',
            datatype: 'boolean',
            helperText: 'Adds a gallery picker alongside the camera.',
          },
          {
            field_name: 'show_box',
            label: 'Show framing box on the camera',
            datatype: 'boolean',
          },
          REQUIRED_FIELD,
        ],
      },
      {
        id: 21,
        name: 'audio',
        iconClass: 'fas fa-microphone',
      },
      {
        id: 18,
        name: 'docscan',
        iconClass: 'far fa-file-alt',
        attribsField: [SCANTYPE_FIELD, DOCSIZE_FIELD, REQUIRED_FIELD],
      },
      /* 
                   {
                    id: 20,
                    name: 'docscan_w_barcode',
                    iconClass: 'far fa-file-alt',
                  }, */
      {
        id: 19,
        name: 'multiselect',
        iconClass: 'far fa-list-alt',
        defineResponses: true,
      },
      {
        id: 22,
        name: 'web_link_qr',
        iconClass: 'fa fa-qrcode',
        defineResponses: false,
        // Every attrib key becomes a query param on the redirect URL. `r` is
        // the region/campaign code used in the field; other keys stay editable
        // via Advanced (raw JSON).
        attribsField: [
          {
            field_name: 'r',
            label: 'Region / Campaign Code',
            datatype: 'string',
            placeholder: 'pa',
            helperText: 'Passed through as the `r` query param on the QR link.',
          },
        ],
      },
      {
        id: 23,
        name: 'randomizer',
        iconClass: 'fas fa-random',
        defineResponses: true,
        defineWeights: true,
        attribsField: [REQUIRED_FIELD],
      },
      {
        id: 24,
        name: 'voter_lookup',
        iconClass: 'fas fa-address-book',
        defineResponses: false,
        attribsField: [...VOTER_SELECTION_FIELDS, REQUIRED_FIELD],
      },
      {
        id: 25,
        name: 'voterrouted',
        iconClass: 'fas fa-random',
        defineResponses: true,
        defineWeights: true,
        attribsField: [...VOTER_SELECTION_FIELDS, REQUIRED_FIELD],
      },
      {
        id: 27,
        name: 'audio_start',
        iconClass: 'fas fa-headphones',
        attribsField: [
          {
            field_name: 'controls',
            label: 'Show Controls',
            datatype: 'boolean',
            helperText: 'Gives the canvasser recording controls in the app bar.',
          },
        ],
      },
      {
        id: 28,
        name: 'audio_end',
        iconClass: 'fas fa-headphones',
      },
      {
        id: 29,
        name: 'nearest_neighbor',
        iconClass: 'fas fa-map-marker-alt',
        attribsField: [
          {
            field_name: 'neighbor_set',
            label: 'Neighbor Set',
            datatype: 'string',
            placeholder: 'mi_voterev_20260506',
            helperText: 'Required. The voter file the lookup searches.',
          },
          {
            field_name: 'radius_size',
            label: 'Search Radius (meters)',
            datatype: 'number',
            default: 500,
            helperText: 'Defaults to 500 when left empty.',
          },
          ...VOTER_SELECTION_FIELDS,
          REQUIRED_FIELD,
        ],
      },
      {
        id: 30,
        name: 'contact',
        iconClass: 'fas fa-address-card',
        attribsField: [REQUIRED_FIELD],
      },
      {
        id: 31,
        name: 'image_redactor',
        iconClass: 'far fa-image',
        attribsField: [
          {
            field_name: 'redactionJsonPath',
            label: 'Descriptor Path',
            datatype: 'string',
            helperText: 'Leave empty to use the descriptor bundled with the app.',
          },
          REQUIRED_FIELD
        ],
      }
    ];
  }

  componentDidMount() {
    this.loadScript(this.props.params.script_id);
  }

  componentDidUpdate(prevProps) {
    // When we navigate to a freshly-cloned script the route param changes but
    // this component instance is reused, so reload the new script's data.
    if (prevProps.params.script_id !== this.props.params.script_id) {
      this.setState({ editing_element: null });
      this.loadScript(this.props.params.script_id);
    }
  }

  loadScript = (script_id) => {
    this.setState({ loading: true });
    api
      .get('/script/', { s: script_id })
      .then((res) => {
        console.log(res.data);
        this.onLoadScriptData(res.data);
      })
      .finally(() => {
        this.setState({ loading: false });
      });
    // this.onLoadScriptData(sampleData)
  };

  _isError = () => {
    return this.state.error;
  };

  _isLoading = () => {
    return this.state.loading;
  };

  onCreateElementRequest = (element_type_config) => {
    this.setState({
      editing_element: { element_type_config },
    });
  };

  onEditElementRequest = (element) => {
    //set to null and then back to make sure it updates if we were just updating another element.
    this.setState({ editing_element: null }, () =>
      this.setState({
        editing_element: {
          ...element,
          element_type_config: this.elementTypes.find((d) => d.name === element.elementtype) || {},
        },
      })
    );
  };

  onLoadScriptData = (script_data, revision_only = false) => {
    let script_metadata = script_data.script;
    let script_revisions = script_data.drafts;

    this.onLoadRevision(script_data.revision);

    this.setState({
      script_metadata,
      script_revisions,
      displayed_revision: script_metadata.draft_revision_id,
    });
  };

  onLoadRevision = (revision_data) => {
    const script_elements = revision_data.script_schema.map((d) => {
      // START node (special)
      if (d.elementtype === 'start') {
        return {
          ...d,
          iconClass: 'fas fa-play-circle',
          isStart: true,
          pos_top: Number.isFinite(+d.pos_top) ? d.pos_top : 100,
          pos_left: Number.isFinite(+d.pos_left) ? d.pos_left : 100,
          connectors: d.connectors || [],
        };
      }

      // Normal nodes
      const match = this.elementTypes.find((e) => e.name === d.elementtype);

      return {
        ...d,
        iconClass: match?.iconClass,
        elementtype_id: match?.id,
        pos_top: Number.isFinite(+d.pos_top) ? d.pos_top : 100,
        pos_left: Number.isFinite(+d.pos_left) ? d.pos_left : 100,
        connectors: d.connectors || [],
      };
    });

    this.setState({ script_elements });
  };

  onSaveRevisionRequest = () => {
    this.setState({ loading: true });
    api
      .post('/script/', {
        schema: this.state.script_elements.map((el) =>
          omit(el, ['element_type_config'])
        ),
        script_id: this.state.script_metadata.id,
      })
      .then((res) => {
        this.setState({
          script_revisions: [res.data, ...this.state.script_revisions],
          displayed_revision: res.data.id,
        });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  /* -----------------------------
   * Copy Script
   * ----------------------------- */

  onOpenCopyModal = () => {
    this.setState({
      copyModalOpen: true,
      copyTargetCampaign:
        this.state.copyTargetCampaign || this.props.focusedCampaign?.id,
      copyName: `${this.state.script_metadata.name || 'Script'} Copy`,
    });
  };

  onCloseCopyModal = () => {
    this.setState({ copyModalOpen: false });
  };

  onCopyScriptSubmit = () => {
    const { copyTargetCampaign, copyName } = this.state;
    if (!copyTargetCampaign) return;

    this.setState({ copying: true });
    api
      .post('/script/copy/', {
        script_id: this.state.script_metadata.id,
        target_campaign_id: copyTargetCampaign,
        source_revision_id: this.state.displayed_revision,
        name: copyName,
      })
      .then((res) => {
        const { script_id, campaign_id } = res.data;
        // Switch focus to the target campaign so subsequent calls (and the
        // auto-injected campaign param) line up with the new script.
        if (campaign_id && campaign_id !== this.props.focusedCampaign?.id) {
          setFocusedMembership(campaign_id);
        }
        this.setState({ copyModalOpen: false });
        this.props.navigate(`/script/${script_id}/edit`);
      })
      .catch((err) => {
        console.error('Failed to copy script', err);
        this.setState({ error: true, errMsg: 'Failed to copy the script.' });
        setTimeout(() => this.setState({ error: false }), 4000);
      })
      .finally(() => {
        this.setState({ copying: false });
      });
  };

  onCreateEditElementSubmit = (element) => {
    this.setState((prev) => {
      const idx = prev.script_elements.findIndex((d) => d.element_hash === element.element_hash);

      let script_elements;
      if (idx >= 0) {
        script_elements = prev.script_elements.map((d, i) => (i === idx ? { ...element } : d));
      } else {
        script_elements = [...prev.script_elements, { ...element }];
      }

      return {
        editing_element: null,
        script_elements,
      };
    });
  };

  onDeleteElement = () => {
    let { script_elements: ses, editing_element: element } = this.state;

    ses = ses.filter((d) => d.element_hash !== element.element_hash);
    ses.forEach((d) => {
      d.connectors = d.connectors.map((c) =>
        c.next_el_hash === element.element_hash ? { ...c, next_el_hash: null } : c
      );
    });

    this.setState({ script_elements: ses, editing_element: null });
  };

  onDeleteResponseOption = (element_hash, connector) => { };

  handleElementMove = (element_hash, x, y) => {
    this.setState((prev) => ({
      script_elements: prev.script_elements.map((d) =>
        d.element_hash === element_hash ? { ...d, pos_left: x, pos_top: y } : d
      ),
    }));
  };

  _renderCreationButtons = () => {
    return (
      <>
        <div className={`side-menu-list-obj`}>
          <h6>
            Click on an element type below to add it. Double click on an existing element to edit
            it.{' '}
          </h6>
        </div>
        {this.elementTypes.map((d) => (
          <div
            className={`side-menu-list-obj`}
            key={d.name}
            onClick={() => this.onCreateElementRequest(d)}
          // onMouseEnter={this.props.onMouseEnter}
          // onMouseLeave={this.props.onMouseLeave}
          >
            <div className="list-obj-inner-wrapper" style={{ borderLeftColor: 'transparent' }}>
              <div className="obj-title">
                <i className={d.iconClass + ' mr-3'} />
                {d.name === 'voterrouted' ? 'Voter Routed' : (d.name === 'image_redactor' ? 'Form Image Scan' : startCase(d.name))}
              </div>
            </div>
          </div>
        ))}
      </>
    );
  };

  _renderMenuBody = () => {
    if (this.state.editing_element) {
      return (
        <CreateElementForm
          {...this.state}
          onSubmit={this.onCreateEditElementSubmit}
          onCancel={() => this.setState({ editing_element: null })}
          onDeleteElement={this.onDeleteElement}
        />
      );
    } else {
      return this._renderCreationButtons();
    }
  };


  _handleRevisionSelection = (revision_id) => {
    // the select gives us a string; keep it numeric so comparisons against
    // pub_revision_id / draft_revision_id (numbers) stay accurate.
    this.setState({ displayed_revision: Number(revision_id) });

    api.get('/script-get-schema/', { sr: revision_id }).then((res) => {
      this.onLoadRevision(res.data);
    });
  };

  _renderMenu = () => {
    return (
      <div className={`map-side-menu script-edit-sidebar ${this.state.sideBarClosed && 'closed'}`}>
        <div className="side-menu-wrapper" id="side-menu-scroll-wrapper">
          <Stack spacing={1.25} className="side-menu-title script-edit-sidebar__header">


            <Button
              component={Link}
              to="/script"
              size="small"
              startIcon={<i className="fas fa-arrow-left" />}
              color="primary"
              variant="outlined"
            >
              All Scripts
            </Button>


            <Stack direction="row" gap={1} fullWidth  alignItems="center" justifyContent="space-between">
              <Button
                size="small"
                color="secondary"
                variant="outlined"
                startIcon={<i className="far fa-copy" />}
                onClick={this.onOpenCopyModal}
              >
                Copy
              </Button>
              <Typography variant="subtitle1" color="primary.main" >
                {this.state.script_metadata.name}
              </Typography>
 
            </Stack>

            <Stack direction="row" gap={1} fullWidth justifyContent="flex-end" alignItems="flex-end">
              <Button
                size="small"
                color="primary"
                variant="outlined"
                startIcon={<i className="fas fa-save" />}
                disabled={this._isLoading()}
                onClick={this.onSaveRevisionRequest}
              >
                Save
              </Button>
              <TextField
                labelId="script-revision-select-label"
                select
                fullWidth
                variant="standard"
                fontSize="small"
                size='small'
                value={this.state.displayed_revision ?? ''}
                onChange={(e) => this._handleRevisionSelection(e.target.value)}
              >
                {this.state.script_revisions.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {this.state.script_metadata.pub_revision_id === d.id ? '[ PUBLISHED ] ' : ''}
                    Revision {d.revision_num} ({new Moment(d.created_at).format('MM/DD LT')})
                  </MenuItem>
                ))}
              </TextField>

            </Stack>
          </Stack>
          <Box className="side-menu-body">{this._renderMenuBody()}</Box>
        </div>

        <div className={`side-menu-select-wrapper`} style={{ width: '1px' }}>
          {this.state.loading && (
            <div className={`side-menu-option loading`}>
              <i className="fas fa-asterisk" />
            </div>
          )}
        </div>
      </div>
    );
  };

  render() {
    return (
      <div>
        <FloatHeader link="/script" />

        <div className={'polka-back script-edit-wrapper script-display-root'}>
          <ScriptDisplay
            data={this.state.script_elements}
            is_editable={true}
            onEditElementRequest={this.onEditElementRequest}
            editingElement={this.state.editing_element}
            onElementMove={this.handleElementMove}
            onLinkElementComplete={(updatedData) => {
              this.setState({ script_elements: updatedData });
            }}
          />
        </div>

        {this._renderMenu()}

        {this._isError() && (
          <FeedbackTile
            text={
              <div>
                <i className="fas fa-exclamation-triangle" style={{ marginRight: '15px' }} />
                {this.state.errMsg}
              </div>
            }
            visible={true}
            classNames="danger clear-sidebar-feedback-tile"
          />
        )}

        {/* COPY SCRIPT MODAL */}
        <BaseModal
          title="Copy Script"
          open={this.state.copyModalOpen}
          onClose={this.onCloseCopyModal}
          loading={this.state.copying}
          center
        >
          <div className="modal-internal-wrapper mx-5">
            <p className={'mb-2'}>
              Copy <b>{this.state.script_metadata.name}</b> (and all its elements,
              connectors, and attributes) into a campaign.
            </p>

            {this.state.displayed_revision !==
              this.state.script_metadata.pub_revision_id && (
                <p className={'mb-3'} style={{ color: '#b7791f' }}>
                  <i className={'fas fa-exclamation-triangle mr-2'} />
                  The revision you are viewing is not the published one. The copy
                  will be made from this active revision.
                </p>
              )}

            <label className={'is-size-7 mb-1'}>Script Name</label>
            <Input
              className={'m-0 mb-3'}
              type={'text'}
              value={this.state.copyName}
              onChange={(val) => this.setState({ copyName: val })}
            />

            <label className={'is-size-7 mb-1'}>Target Campaign</label>
            <Input
              className={'m-0 mb-4'}
              type={'select'}
              options={(this.props.campaigns || []).map((c) => ({
                value: c.id,
                display: c.name,
              }))}
              value={this.state.copyTargetCampaign}
              onChange={(val) => this.setState({ copyTargetCampaign: +val })}
            />

            <div className="button-wrapper">
              <div
                className={`button ${this.state.copying ? 'disabled' : ''}`}
                onClick={this.onCopyScriptSubmit}
              >
                Copy
              </div>
              <div
                className="button button-outline"
                onClick={this.onCloseCopyModal}
              >
                Cancel
              </div>
            </div>
          </div>
        </BaseModal>

        {/*<BaseModal*/}
        {/*    title={`Edit ${this.state.editingInstance.name}`}*/}
        {/*    open={this.state.editModalOpen}*/}
        {/*    onClose={this.onCloseEditModal}*/}
        {/*    center*/}
        {/*>*/}
        {/*    <BaseForm*/}
        {/*        inputs={this.editFormInputs}*/}
        {/*        onCancel={this.onCloseEditModal}*/}
        {/*        onSubmit={this.handleUpdateCreateSubmission}*/}
        {/*    />*/}
        {/*</BaseModal>*/}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  campaigns: state.campaigns?.data || [],
  focusedCampaign: state.session.focusedCampaign,
});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ScriptEdit));

/*
* Speech
Question
Open Question
Phone
Email
Signature
Barcode
Image
*
* */

const sampleData = {
  id: '17',
  name: 'Missourians for Healthcare',
  script_elements: [
    {
      element_id: 227,
      display_name: 'START',
      element_hash: 'start',
      pos_top: 1015.0,
      pos_left: 990.0,
      prompt: '',
      elementtype: 'start',
      connectors: [
        {
          ordinal_position: 1,
          next_el: 228,
          next_el_display_name: 'Scan Barcode',
          next_el_hash: 'u949c',
        },
      ],
      response_count: 0,
    },
    {
      element_id: 228,
      display_name: 'Scan Barcode',
      element_hash: 'u949c',
      pos_top: 1147.0,
      pos_left: 1041.0,
      prompt: '',
      elementtype: 'barcode',
      connectors: [
        {
          ordinal_position: 1,
          next_el: 229,
          next_el_display_name: 'Line Number',
          next_el_hash: '7ietk',
        },
      ],
      response_count: 0,
    },
    {
      element_id: 229,
      display_name: 'Line Number',
      element_hash: '7ietk',
      pos_top: 1300.0,
      pos_left: 1100.0,
      prompt: '',
      elementtype: 'question',
      connectors: [
        {
          ordinal_position: 1,
          next_el: 230,
          next_el_display_name: 'Cell Phone Number',
          next_el_hash: '7rooe',
          value_text: '1',
        },
        {
          ordinal_position: 2,
          next_el: 230,
          next_el_display_name: 'Cell Phone Number',
          next_el_hash: '7rooe',
          value_text: '2',
        },
        {
          ordinal_position: 3,
          next_el: 230,
          next_el_display_name: 'Cell Phone Number',
          next_el_hash: '7rooe',
          value_text: '3',
        },
        {
          ordinal_position: 4,
          next_el: 230,
          next_el_display_name: 'Cell Phone Number',
          next_el_hash: '7rooe',
          value_text: '4',
        },
        {
          ordinal_position: 5,
          next_el: 230,
          next_el_display_name: 'Cell Phone Number',
          next_el_hash: '7rooe',
          value_text: '5',
        },
        {
          ordinal_position: 6,
          next_el: 230,
          next_el_display_name: 'Cell Phone Number',
          next_el_hash: '7rooe',
          value_text: '6',
        },
        {
          ordinal_position: 7,
          next_el: 230,
          next_el_display_name: 'Cell Phone Number',
          next_el_hash: '7rooe',
          value_text: '7',
        },
        {
          ordinal_position: 8,
          next_el: 230,
          next_el_display_name: 'Cell Phone Number',
          next_el_hash: '7rooe',
          value_text: '8',
        },
        {
          ordinal_position: 9,
          next_el: 230,
          next_el_display_name: 'Cell Phone Number',
          next_el_hash: '7rooe',
          value_text: '9',
        },
        {
          ordinal_position: 10,
          next_el: 230,
          next_el_display_name: 'Cell Phone Number',
          next_el_hash: '7rooe',
          value_text: '10',
        },
      ],
      response_count: 0,
    },
    {
      element_id: 230,
      display_name: 'Cell Phone Number',
      element_hash: '7rooe',
      pos_top: 1469.0,
      pos_left: 1438.0,
      prompt: 'Enter voters cell phone number for campaign updates.',
      elementtype: 'phone',
      connectors: [],
      response_count: 0,
    },
  ],
};
